/**
 * WebSocket Service for ADDAPT AI Chat
 * Handles real-time communication with the AI backend
 */

import { Auth } from 'aws-amplify';

export interface WebSocketMessage {
  action?: string;
  type?: 'typing' | 'message' | 'error' | 'connected' | 'disconnected';
  message?: string;
  sessionId?: string;
  documentContext?: any;
  timestamp?: Date;
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
  status?: 'sending' | 'sent' | 'error';
}

class ADDAPTChatClient {
  private ws: WebSocket | null = null;
  private apiEndpoint: string;
  private accessToken: string | null = null;
  private sessionId: string;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private isAuthenticated = false;
  
  // Event handlers
  public onMessage?: (data: WebSocketMessage) => void;
  public onConnect?: () => void;
  public onDisconnect?: (code?: number, reason?: string) => void;
  public onError?: (error: any) => void;
  public onTyping?: () => void;
  
  constructor() {
    this.apiEndpoint = this.getWebSocketEndpoint();
    this.sessionId = this.generateSessionId();
  }
  
  /**
   * Get WebSocket endpoint from settings
   */
  private getWebSocketEndpoint(): string {
    const settings = (window as any).__ADDAPT_SETTINGS__;
    const wsEndpoint = settings?.webSocketConfig?.default || settings?.webSocketConfig?.ai;
    
    if (!wsEndpoint) {
      console.error('WebSocket endpoint not configured in settings');
      // Fallback to beta endpoint
      return 'wss://ury78kirr5.execute-api.us-east-1.amazonaws.com/beta';
    }
    
    return wsEndpoint;
  }
  
  /**
   * Authenticate and get access token
   */
  private async authenticate(): Promise<string> {
    try {
      const session = await Auth.currentSession();
      const accessToken = session.getAccessToken().getJwtToken();
      this.accessToken = accessToken;
      this.isAuthenticated = true;
      return accessToken;
    } catch (error) {
      console.error('Authentication failed:', error);
      this.isAuthenticated = false;
      throw new Error('Failed to authenticate user');
    }
  }
  
  /**
   * Connect to WebSocket
   */
  public async connect(): Promise<void> {
    try {
      // Get fresh token
      const token = await this.authenticate();
      
      // Pass token as query parameter (WebSocket doesn't support headers)
      const wsUrl = `${this.apiEndpoint}?token=${encodeURIComponent(token)}`;
      
      console.log('Connecting to WebSocket:', this.apiEndpoint);
      this.ws = new WebSocket(wsUrl);
      
      this.setupEventHandlers();
    } catch (error) {
      console.error('Failed to connect:', error);
      this.onError?.(error);
      throw error;
    }
  }
  
  /**
   * Setup WebSocket event handlers
   */
  private setupEventHandlers(): void {
    if (!this.ws) return;
    
    this.ws.onopen = () => {
      this.reconnectAttempts = 0;
      this.onConnect?.();
      
      // Send initial connection message
      this.send({
        action: 'connect',
        sessionId: this.sessionId
      });
    };
    
    this.ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        
        // Handle different message types
        switch (data.type) {
          case 'typing':
            this.onTyping?.();
            break;
          case 'message':
          case 'error':
          case 'connected':
            this.onMessage?.(data);
            break;
          default:
            this.onMessage?.(data);
        }
      } catch (error) {
        console.error('Failed to parse message:', error);
      }
    };
    
    this.ws.onerror = (error) => {
      console.error('❌ WebSocket error:', error);
      this.onError?.(error);
    };
    
    this.ws.onclose = (event) => {
      console.log('WebSocket closed:', event.code, event.reason);
      
      if (event.code === 1008) {
        console.error('❌ Authentication failed - invalid token');
        this.onDisconnect?.(event.code, 'Authentication failed');
        // Don't attempt reconnect for auth failures
        return;
      }
      
      this.onDisconnect?.(event.code, event.reason);
      
      // Attempt reconnection for other close reasons
      if (this.reconnectAttempts < this.maxReconnectAttempts) {
        this.attemptReconnect();
      }
    };
  }
  
  /**
   * Attempt to reconnect
   */
  private attemptReconnect(): void {
    this.reconnectAttempts++;
    console.log(`Attempting reconnect ${this.reconnectAttempts}/${this.maxReconnectAttempts}...`);
    
    setTimeout(() => {
      this.connect().catch(error => {
        console.error('Reconnection failed:', error);
      });
    }, this.reconnectDelay * this.reconnectAttempts);
  }
  
  /**
   * Send message through WebSocket
   */
  public sendMessage(message: string, documentContext?: any): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.error('WebSocket is not connected');
      this.onError?.(new Error('WebSocket is not connected'));
      return;
    }
    
    const payload: WebSocketMessage = {
      action: 'sendMessage',
      message: message,
      sessionId: this.sessionId,
      documentContext: documentContext
    };
    
    this.send(payload);
  }
  
  /**
   * Send raw data through WebSocket
   */
  private send(data: any): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
    }
  }
  
  /**
   * Generate unique session ID
   */
  private generateSessionId(): string {
    return 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }
  
  /**
   * Get connection status
   */
  public isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }
  
  /**
   * Get authentication status
   */
  public isUserAuthenticated(): boolean {
    return this.isAuthenticated;
  }
  
  /**
   * Disconnect from WebSocket
   */
  public disconnect(): void {
    if (this.ws) {
      // Send disconnect message
      this.send({
        action: 'disconnect',
        sessionId: this.sessionId
      });
      
      this.ws.close(1000, 'Client disconnect');
      this.ws = null;
    }
  }
  
  /**
   * Refresh authentication token
   */
  public async refreshToken(): Promise<void> {
    try {
      await this.authenticate();
      
      // Reconnect with new token
      this.disconnect();
      await this.connect();
    } catch (error) {
      console.error('Failed to refresh token:', error);
      throw error;
    }
  }
}

// Export singleton instance
let chatClientInstance: ADDAPTChatClient | null = null;

export const getChatClient = (): ADDAPTChatClient => {
  if (!chatClientInstance) {
    chatClientInstance = new ADDAPTChatClient();
  }
  return chatClientInstance;
};

export default ADDAPTChatClient;
